import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export interface NotificationPreferences {
  monthly_digest: boolean;
  billing_reminders: boolean;
  product_updates: boolean;
}

const DEFAULTS: NotificationPreferences = {
  monthly_digest: true,
  billing_reminders: true,
  product_updates: true,
};

export function useNotificationPreferences() {
  const { user } = useAuth();
  const [prefs, setPrefs] = useState<NotificationPreferences>(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) { setLoading(false); return; }

    supabase
      .from('notification_preferences')
      .select('monthly_digest, billing_reminders, product_updates')
      .eq('user_id', user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (data) setPrefs(data);
        setLoading(false);
      });
  }, [user?.id]);

  const update = useCallback(async (patch: Partial<NotificationPreferences>) => {
    if (!user) return;
    const next = { ...prefs, ...patch };
    setPrefs(next); // optimistic
    setSaving(true);
    await supabase
      .from('notification_preferences')
      .upsert({ user_id: user.id, ...next }, { onConflict: 'user_id' });
    setSaving(false);
  }, [user, prefs]);

  return { prefs, loading, saving, update };
}
